import React, { ReactNode, ReactText, useEffect, useState } from "react";
import { EmailTemplate } from "@/Contracts/EmailTemplates";
import ZModal from "@atoms/ZModal";
import { ModalProps } from "antd";
import ZTable from "@organisms/ZTable";
import { TableRowSelection } from "antd/lib/table/interface";
import ZIcon from "@atoms/ZIcon";
import ZInput from "@molecules/ZInput";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { searchEmailTemplates } from "@/Services/EmailTemplates";
import useDebounce from "use-debounce/lib/useDebounce";
import { ColumnProps } from "antd/lib/table/Column";
import ZTypography from "@atoms/ZTypography";
import { ZButton } from "@atoms";

interface IImportEmailTemplateModalProps extends ModalProps {
	readonly onSelect: (template: EmailTemplate) => void;
}

const ImportEmailTemplateModal: React.FC<
	IImportEmailTemplateModalProps
> = props => {
	const [emailTemplateList, setEmailTemplateList] = useState<EmailTemplate[]>(
		[]
	);

	const [searchString, setSearchString] = useState("");
	const [selectedRowKeys, setSelectedRowKeys] = useState<ReactText[]>([]);

	const [deBouncedSearchString] = useDebounce(searchString, 600);

	const customEmailTemplates = emailTemplateList.filter(
		(template: EmailTemplate) => !template.custom_template_type
	);

	const [selectedTemplate, setSelectedTemplate] =
		useState<EmailTemplate | null>(null);

	const [isLoading, setIsLoading] = useState(false);

	const useSelectedTemplate = () => {
		if (selectedTemplate) {
			props.onSelect(selectedTemplate);
			setSelectedTemplate(null);
		}
	};

	const clearSearch = () => {
		setSearchString("");
	};

	const columns: ColumnProps<EmailTemplate>[] = [
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			render: (_text: unknown, record: EmailTemplate): ReactNode => {
				return (
					<ZTypography.ZText>
						{!!record?.name?.length ? record.name : "--"}
					</ZTypography.ZText>
				);
			}
		}
	];

	const emptyEmailTemplates = () => {
		return (
			<div className="no-templates-found">
				No email templates are present
				<div>
					{deBouncedSearchString.length > 0 && (
						<ZButton size="small" onClick={clearSearch}>
							Clear Search
						</ZButton>
					)}
				</div>
			</div>
		);
	};

	const rowSelection: TableRowSelection<EmailTemplate> = {
		onChange: (
			_selectedEmailTemplateId: ReactText[],
			selectedEmailTemplates: EmailTemplate[]
		) => {
			setSelectedTemplate(selectedEmailTemplates[0]);
			setSelectedRowKeys(
				selectedEmailTemplates.map(template => template.id)
			);
		},
		type: "radio",
		selectedRowKeys
	};

	const searchEmailTemplate = async (keyword = "") => {
		try {
			setIsLoading(true);
			const emailTemplatesResponse = await searchEmailTemplates(keyword);
			setEmailTemplateList(emailTemplatesResponse.data);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (props.visible) {
			searchEmailTemplate();
		}
	}, [props.visible]);

	useEffect(() => {
		if (props.visible) {
			searchEmailTemplate(deBouncedSearchString);
		}
	}, [deBouncedSearchString]);

	return (
		<ZModal
			centered
			destroyOnClose={true}
			title="Email Templates"
			visible={props.visible}
			onCancel={props.onCancel}
			cancelText="Close"
			wrapClassName="import-email-templates-modal"
			size="md"
			okText="Use"
			okButtonProps={{
				onClick: useSelectedTemplate,
				disabled: !!!selectedTemplate
			}}
		>
			{!!customEmailTemplates?.length ? (
				<ZTable<EmailTemplate>
					columns={columns}
					showHeader={true}
					title={() => (
						<ZInput
							type="search"
							placeholder="Search Email Templates"
							suffix={
								!searchString && (
									<ZIcon
										name={EIcons.SEARCH}
										size="sm"
										color={EColors.BLUE}
										colorVariant={7}
									/>
								)
							}
							className="mb-4"
							onChange={e => setSearchString(e.target.value)}
							value={searchString}
							allowClear
						/>
					)}
					rowKey="id"
					pagination={false}
					rowSelection={rowSelection}
					dataSource={customEmailTemplates}
					loading={isLoading}
					size="small"
				/>
			) : (
				emptyEmailTemplates()
			)}
		</ZModal>
	);
};

export default ImportEmailTemplateModal;
